/* eslint-disable camelcase */

import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
import { buildPermissionMap } from '../admin/accessControl';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isSessionExpired: false,
  user: null,
  currentAdmin: null,
  profile: null,
  permissions: [],
  permissionMap: {},
  apps: [],
  modules: [],
  flags: [],
  authError: null,
};

const handlers = {
  INITIALIZE: (state, action) => ({
    ...state,
    isAuthenticated: action.payload.isAuthenticated,
    isInitialized: true,
    isSessionExpired: false,
    user: action.payload.user,
    currentAdmin: action.payload.currentAdmin,
    profile: action.payload.profile,
    permissions: action.payload.permissions,
    permissionMap: action.payload.permissionMap,
    apps: action.payload.apps,
    modules: action.payload.modules,
    flags: action.payload.flags,
    authError: null,
  }),
  LOGIN: (state, action) => ({
    ...state,
    isAuthenticated: true,
    isSessionExpired: false,
    user: action.payload.user,
    currentAdmin: action.payload.currentAdmin,
    profile: action.payload.profile,
    permissions: action.payload.permissions,
    permissionMap: action.payload.permissionMap,
    apps: action.payload.apps,
    modules: action.payload.modules,
    flags: action.payload.flags,
    authError: null,
  }),
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    currentAdmin: null,
    profile: null,
    permissions: [],
    permissionMap: {},
    apps: [],
    modules: [],
    flags: [],
  }),
  AUTH_ERROR: (state, action) => ({
    ...state,
    authError: action.payload.error,
    isSessionExpired: action.payload.httpStatus === 401,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  refreshPermissions: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadPermissionState = async () => {
    const permissionResponse = await axios.get('/access/me/permissions');
    const payload = permissionResponse.data;

    return {
      currentAdmin: payload.current_admin || null,
      profile: payload.profile || null,
      permissions: payload.permissions || [],
      permissionMap: buildPermissionMap(payload.permissions || []),
    };
  };

  const loadShellState = async () => {
    const shellResponse = await axios.get('/control_plane/modules');
    const payload = shellResponse.data || {};

    return {
      apps: payload.apps || [],
      modules: payload.modules || [],
      flags: payload.flags || payload.feature_flags || [],
    };
  };

  const initializeSession = async () => {
    const access_token = window.localStorage.getItem('access_token');

    if (!access_token) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          currentAdmin: null,
          profile: null,
          permissions: [],
          permissionMap: {},
        },
      });
      return;
    }

    setSession(access_token);

    const pingResponse = await axios.get('/ping');
    const { user, access_token: refreshedToken } = pingResponse.data;

    if (refreshedToken) {
      setSession(refreshedToken);
    }

    const [permissionState, shellState] = await Promise.all([loadPermissionState(), loadShellState()]);

    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user,
        ...permissionState,
        ...shellState,
      },
    });
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeSession();
      } catch (error) {
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            currentAdmin: null,
            profile: null,
            permissions: [],
            permissionMap: {},
            apps: [],
            modules: [],
            flags: [],
          },
        });
      }
    };

    const handleAuthError = (event) => {
      const detail = event.detail || {};

      if (detail.httpStatus === 401) {
        setSession(null);
      }

      dispatch({
        type: 'AUTH_ERROR',
        payload: detail,
      });
    };

    window.addEventListener('quickie-admin-auth-error', handleAuthError);
    initialize();

    return () => {
      window.removeEventListener('quickie-admin-auth-error', handleAuthError);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/auth/login', {
      email,
      password,
    });
    const { access_token, user } = response.data;

    setSession(access_token);
    const [permissionState, shellState] = await Promise.all([loadPermissionState(), loadShellState()]);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
        ...permissionState,
        ...shellState,
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/auth/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { access_token, user } = response.data;

    setSession(access_token);
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
        currentAdmin: null,
        profile: null,
        permissions: [],
        permissionMap: {},
        apps: [],
        modules: [],
        flags: [],
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const refreshPermissions = async () => {
    const [permissionState, shellState] = await Promise.all([loadPermissionState(), loadShellState()]);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: state.user,
        ...permissionState,
        ...shellState,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
